import { useState } from "react";

import { apiCall } from "@/api/shared";
import { extractSpotifyTrackId } from "@/utils/spotifyUrlUtils";

// Types for Spotify data
export type SpotifyTrack = {
  id: string;
  name: string;
  duration_ms: number;
  album: {
    release_date?: string;
    images: Array<{
      url: string;
    }>;
  };
  artists: Array<{
    name: string;
  }>;
  external_ids?: {
    isrc?: string;
  };
  popularity?: number;
};

// Backend DTO types (camelCase)
type SpotifyTrackDto = {
  id: string;
  name: string;
  durationMs: number | null;
  album: {
    releaseDate?: string;
    images: Array<{
      url: string;
    }>;
  };
  artists: Array<{
    name: string;
  }>;
  isrc?: string;
  popularity?: number;
};

type SpotifyOperationsOptions = {
  onSuccess?: (data: SpotifyTrack | null) => void;
  onError?: (message: string) => void;
};

type SpotifyOperationsResult = {
  getSpotifyTrackById: (id: string) => Promise<SpotifyTrack | null>;
  getSpotifyTrackByUrl: (url: string) => Promise<SpotifyTrack | null>;
  parseSpotifyUrl: (url: string) => string | null;
  isLoading: boolean;
};

/**
 * Map backend DTO (camelCase) to frontend SpotifyTrack interface (snake_case)
 */
function mapDtoToSpotifyTrack(dto: SpotifyTrackDto): SpotifyTrack {
  return {
    id: dto.id,
    name: dto.name,
    duration_ms: dto.durationMs ?? 0,
    album: {
      release_date: dto.album.releaseDate,
      images: dto.album.images,
    },
    artists: dto.artists,
    external_ids: dto.isrc != null ? { isrc: dto.isrc } : undefined,
    popularity: dto.popularity,
  };
}

/**
 * Custom hook for Spotify operations
 * Now routes all calls through the backend proxy to avoid exposing Spotify tokens to the browser
 */
export function useSpotifyOperations({
  onSuccess = () => {},
  onError = () => {},
}: SpotifyOperationsOptions = {}): SpotifyOperationsResult {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Parse a Spotify URL to get the track ID
   * Now supports internationalized URLs like https://open.spotify.com/intl-pt/track/ID
   */
  const parseSpotifyUrl = (url: string): string | null => {
    return extractSpotifyTrackId(url);
  };

  /**
   * Get a Spotify track by ID via the backend proxy
   * Calls GET /rightsapp/get?path=/api/v1/spotify/track/{id}
   */
  const getSpotifyTrackById = async (id: string): Promise<SpotifyTrack | null> => {
    setIsLoading(true);

    try {
      const responseJson = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/track/${id}`,
      }) as { track: SpotifyTrackDto } | null;

      if (!responseJson?.track) {
        throw new Error("Backend returned unexpected response shape");
      }

      const track = mapDtoToSpotifyTrack(responseJson.track);
      onSuccess(track);
      return track;
    }
    catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : "Unknown error fetching Spotify track";

      console.error("Error fetching Spotify track:", errorMessage);
      onError(errorMessage);
      return null;
    }
    finally {
      setIsLoading(false);
    }
  };

  /**
   * Parse Spotify URL and get track via backend
   */
  const getSpotifyTrackByUrl = async (url: string): Promise<SpotifyTrack | null> => {
    const trackId = parseSpotifyUrl(url);

    if (trackId === null) {
      const errorMessage = "Could not parse track ID from URL";
      console.error("Error parsing Spotify URL:", errorMessage);
      onError(errorMessage);
      return null;
    }

    return getSpotifyTrackById(trackId);
  };

  return {
    getSpotifyTrackById,
    getSpotifyTrackByUrl,
    parseSpotifyUrl,
    isLoading,
  };
}
